import { mergeFilters, setFilter } from 'actions/search';
import {
  aiFollowUpApplied,
  aiSentConversationMessage,
  rebaselineAiFollowUp,
  userSentConversationMessage,
} from 'actions/searchNLP';
import { Alert } from 'antd';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  getConversationalMessages,
  getFollowUpQueries,
} from 'selectors/searchNLP';
import styled from 'styled-components';
import { ThinkingIndicator } from 'components/common';
import { colours } from 'utils/theme';
import { NLPConversationBubble } from './NLPConversationBubble';
import * as strings from './strings';
import { MAX_FOLLOWUP_ROUNDS, useFollowUpQueries } from './useFollowUpQueries';
// SCIL-15848 — follow-up queries are live. The panel hydrates from POST /api/ai/followups
// after each non-zero search; clicking a follow-up applies its pre-computed additive
// filters and re-runs the search with no extra LLM round-trip.
export const FOLLOWUP_QUERIES_ENABLED = true;

// Wraps both the thinking indicator and the heading+chips so they share one divider under
// the conversation, reading as a single grouped follow-up turn rather than a separate block.
const FollowUpSection = styled.div`
  margin-top: 16px;
  padding: 16px 0px;
  border-top: 1px solid ${colours.borderGreyLight};
`;

const FollowUpHeading = styled.div`
  font-size: 14px;
  font-weight: 700;
  color: ${colours.character.primary85};
  margin-bottom: 12px;
`;

const ChipList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Placeholder = styled.div`
  padding: 20px;
  text-align: center;
  font-size: 14px;
  color: ${colours.darkGrey};
`;

// Bounds the conversation AND the follow-up section (thinking indicator / heading+chips) together
// as one scrollable unit. They used to be capped separately — only the conversation had a max
// height — so a tall follow-up section (long chip text, a small window) had nowhere bounded to
// live and could grow past the visible area instead of scrolling with the rest.
const ScrollArea = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-top: 8px;
  padding-bottom: 8px;
  min-height: 100px;
  max-height: 460px;
  overflow-y: auto;
`;

export const NLPConversationView = () => {
  let msgs = useSelector(getConversationalMessages);
  let err = null;
  if (!msgs) {
    msgs = [];
    err = 'Message list was malformed.';
  }

  if (Boolean(err)) {
    return <Alert type='warning' showIcon message={err} />;
  }

  if (!Boolean(msgs) || msgs.length <= 0) {
    return null;
  }

  return msgs.map(msg => (
    <NLPConversationBubble key={msg.key} isUser={msg.isUser}>
      {msg.messageText}
    </NLPConversationBubble>
  ));
};

/**
 * SciSearch AI tab showing the follow-up query suggestions for the current search.
 */
export const NLPSearchTab = () => {
  const dispatch = useDispatch();
  const {
    followUps,
    loading,
    isSearchRerunning,
    resumedFromSavedSearch,
    isCurrent,
    sessionId,
    searchFailed,
  } = useFollowUpQueries({
    enabled: FOLLOWUP_QUERIES_ENABLED,
  });

  const stored = useSelector(getFollowUpQueries);
  const conversationMessages = useSelector(getConversationalMessages);
  const messageCount = Array.isArray(conversationMessages)
    ? conversationMessages.length
    : 0;

  const scrollAreaRef = React.useRef(null);
  // Keep the panel pinned to the bottom as new content lands — a new conversation bubble, the
  // thinking indicator appearing, or a batch of follow-up chips — so the latest activity is always
  // visible rather than appended below the fold. Fires only when one of those genuinely changes
  // (not on every render), so it won't yank the user back down if they scroll up to read history
  // while nothing new is arriving.
  React.useEffect(() => {
    if (scrollAreaRef.current) {
      scrollAreaRef.current.scrollTop = scrollAreaRef.current.scrollHeight;
    }
  }, [messageCount, loading, followUps]);

  const applyFollowUp = followUp => {
    // Follow-ups merge additively (SCIL-16857), except location keys: region+country share one
    // OR'd array, so a narrowing follow-up replaces them rather than stacking (SCIL-17056).
    // Tradeshow mode is exclusive too (thisShowOnly vs presentingAtTradeshow/exhibitingShow), so an
    // incoming mode replaces the conflicting one instead of stacking.
    const additive = { ...(followUp.filters ?? {}) };
    ['organisationGeography', 'tradeshowCountryRegion'].forEach(key => {
      if (additive[key] !== undefined) {
        dispatch(setFilter(key, additive[key]));
        delete additive[key];
      }
    });
    const tradeshowModes = [
      'thisShowOnly',
      'presentingAtTradeshow',
      'exhibitingShow',
    ];
    if (tradeshowModes.some(key => additive[key] !== undefined)) {
      if (additive.thisShowOnly) {
        // Narrow wins: SET_FILTER('thisShowOnly', true) clears presentingAtTradeshow + exhibitingShow.
        dispatch(setFilter('thisShowOnly', true));
      } else {
        // Broad mode(s): apply them before dropping the narrow flag, so the reducer's
        // untick-defaults-to-Presenting rule sees a mode already set and leaves it alone.
        ['presentingAtTradeshow', 'exhibitingShow'].forEach(key => {
          if (additive[key] !== undefined)
            dispatch(setFilter(key, additive[key]));
        });
        dispatch(setFilter('thisShowOnly', undefined));
      }
      tradeshowModes.forEach(key => delete additive[key]);
    }
    dispatch(mergeFilters(additive));
    // Re-baseline so the re-run still carries the AI session id and writes fresh insights,
    // keeping the follow-up chain alive. Must run after the filter apply, before refreshSearch.
    dispatch(rebaselineAiFollowUp());
    dispatch(setFilter('refreshSearch', true));
    dispatch(userSentConversationMessage(followUp.text));
    // A follow-up click genuinely re-runs the search, so the same acknowledgement bubble
    // used for the initial search kickoff applies to every refinement round too.
    dispatch(aiSentConversationMessage(strings.followUpFetchingResults));
    // Appends to the query chain AND the feedback event log in one action.
    dispatch(aiFollowUpApplied(followUp));
  };

  // A fetch that failed this round (any cause) — keyed to the current session + fingerprint via
  // isCurrent, so a stale failure from a moved-on search can't trip it.
  const failed = isCurrent && stored.error;

  // Text shown once loading has finished and this round has no chips to display. Reads explicit
  // signals rather than inferring from coincidental equality: without a session no search has run,
  // so the pre-search placeholder is the only correct state (and this is what stops a stale
  // "none found" showing after a Reset nulls the active session).
  const emptyStateText = React.useMemo(() => {
    if (resumedFromSavedSearch) return strings.followUpQuerySavedSearch;
    if (!sessionId) return strings.followUpQueryRunToSee;
    // The search itself errored, so there are no results to refine — say so generically rather
    // than blaming follow-up generation, which never got as far as running.
    if (searchFailed) return strings.searchFailedError;
    // A failed fetch (any cause) — a distinct message telling the user to start a new search,
    // never the pre-search placeholder.
    if (failed) return strings.followUpQueryError;
    // Fetched this round already and got nothing back, as opposed to not having fetched yet.
    if (isCurrent) return strings.followUpQueryNoneFound;
    if (stored.count >= MAX_FOLLOWUP_ROUNDS)
      return strings.followUpQueryMaxRounds;
    // A search ran under this session but no fetch fired for it — too few results to refine.
    return strings.followUpQueryNotEnoughResults;
  }, [
    resumedFromSavedSearch,
    sessionId,
    searchFailed,
    failed,
    isCurrent,
    stored,
  ]);

  // The conversation always renders — the tab is auto-selected after an AI search even in
  // environments where follow-up queries are blocked off, and must not be a blank pane there.
  return (
    <div data-testid='nlp-search-tab'>
      <ScrollArea ref={scrollAreaRef}>
        <NLPConversationView />
        {FOLLOWUP_QUERIES_ENABLED &&
          (loading ? (
            <FollowUpSection>
              <ThinkingIndicator
                key={isSearchRerunning ? 'searching' : 'generating'}
                phrases={
                  isSearchRerunning
                    ? strings.followUpSearchingPhrases
                    : strings.followUpGeneratingPhrases
                }
              />
            </FollowUpSection>
          ) : followUps.length > 0 ? (
            <FollowUpSection>
              <FollowUpHeading>{strings.followUpQueryHeading}</FollowUpHeading>
              <ChipList>
                {followUps.map((followUp, idx) => (
                  <NLPConversationBubble
                    key={idx}
                    suggestion
                    fullWidth
                    onClick={() => applyFollowUp(followUp)}
                    testId={`nlp-followup-${followUp.text}`}
                  >
                    {followUp.text}
                  </NLPConversationBubble>
                ))}
              </ChipList>
            </FollowUpSection>
          ) : messageCount > 0 ? (
            // Group the empty-state message (max rounds reached, none found, etc.) with the
            // conversation under the same divider, matching the thinking / chips states. Only when
            // a conversation exists above it — otherwise the rule would float with nothing above.
            <FollowUpSection>
              <Placeholder>{emptyStateText}</Placeholder>
            </FollowUpSection>
          ) : (
            <Placeholder>{emptyStateText}</Placeholder>
          ))}
      </ScrollArea>
    </div>
  );
};
